/**
 * FeedbackWidget Component (Phase 21.9)
 *
 * Shared widget for operator feedback on assessments and training.
 * Shows thumbs up/down, optional comment, and anonymity toggle.
 *
 * States:
 *  1. Loading -- checking existing feedback
 *  2. Form -- thumbs buttons + anonymity toggle + optional comment + submit
 *  3. Submitting -- spinner, inputs disabled
 *  4. Submitted -- checkmark + "Thanks for your feedback"
 *  5. Already submitted -- same as submitted (loaded from API)
 */

import { useState } from 'react';
import {
  ThumbsUp,
  ThumbsDown,
  Check,
  Loader2,
  EyeOff,
} from 'lucide-react';
import { useCheckFeedback, useSubmitFeedback } from '../hooks/useOperatorFeedback';
import type { OperatorFeedbackType, OperatorFeedbackSentiment } from '@guild/shared';

// ============================================
// TYPES
// ============================================

interface FeedbackWidgetProps {
  /** The assessment or assignment ID */
  targetId: string;
  /** Type of feedback */
  feedbackType: OperatorFeedbackType;
  /** Question shown above the thumbs buttons */
  questionText: string;
  /** Compact mode: no section header, no anonymity toggle, tighter spacing */
  compact?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const COMMENT_MAX_LENGTH = 500;

// ============================================
// COMPONENT
// ============================================

export function FeedbackWidget({
  targetId,
  feedbackType,
  questionText,
  compact = false,
}: FeedbackWidgetProps) {
  // ---- State ----
  const [selectedSentiment, setSelectedSentiment] = useState<OperatorFeedbackSentiment | null>(null);
  const [comment, setComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // ---- Hooks ----
  const { data: existingFeedback, isLoading } = useCheckFeedback(targetId, feedbackType);
  const submitMutation = useSubmitFeedback();

  // ---- Derived State ----
  const hasExistingFeedback = existingFeedback !== null && existingFeedback !== undefined;
  const isSubmitted = hasExistingFeedback || justSubmitted;

  // ---- Handlers ----
  function handleSubmit() {
    if (!selectedSentiment) return;

    submitMutation.mutate(
      {
        feedbackType,
        targetId,
        sentiment: selectedSentiment,
        comment: comment.trim() || undefined,
        isAnonymous,
      },
      {
        onSuccess: () => {
          setJustSubmitted(true);
        },
      }
    );
  }

  // ---- Loading State ----
  if (isLoading) {
    return (
      <div className={compact ? 'py-2' : 'py-4'}>
        <div className="flex items-center justify-center gap-2 text-op-text-tertiary">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-xs">Loading...</span>
        </div>
      </div>
    );
  }

  // ---- Submitted State ----
  if (isSubmitted) {
    const displaySentiment = existingFeedback?.sentiment ?? selectedSentiment;

    return (
      <div className={compact ? 'py-2' : 'py-3'}>
        {!compact && (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-op-text-tertiary">
            Your Feedback
          </p>
        )}
        <div className="flex items-center gap-2 rounded-xl bg-guild-mint/20 px-3 py-2.5">
          <Check size={16} strokeWidth={2} className="flex-shrink-0 text-guild-green" />
          <span className="text-sm font-medium text-guild-green">
            Thanks for your feedback
          </span>
          {displaySentiment && (
            <span className="ml-auto">
              {displaySentiment === 'positive' ? (
                <ThumbsUp size={16} strokeWidth={1.5} className="text-guild-green" />
              ) : (
                <ThumbsDown size={16} strokeWidth={1.5} className="text-red-500" />
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ---- Form State ----
  const isSubmitting = submitMutation.isPending;
  const commentLength = comment.length;
  const isNearLimit = commentLength >= 450;
  const isAtLimit = commentLength >= COMMENT_MAX_LENGTH;

  return (
    <div className={compact ? 'py-2' : 'py-3'}>
      {/* Section Header */}
      {!compact && (
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-op-text-tertiary">
          Your Feedback
        </p>
      )}

      {/* Question */}
      <p className="mb-3 text-sm font-medium text-op-text-primary">
        {questionText}
      </p>

      {/* Thumbs Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => setSelectedSentiment('positive')}
          className={[
            'flex h-11 w-11 items-center justify-center rounded-full transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
            selectedSentiment === 'positive'
              ? 'bg-guild-green text-white scale-110 shadow-sm'
              : 'bg-gray-100 text-op-text-tertiary hover:bg-gray-200 active:scale-95',
            isSubmitting && 'opacity-50 cursor-not-allowed',
          ].join(' ')}
          aria-label="Thumbs up"
        >
          <ThumbsUp size={20} strokeWidth={1.5} />
        </button>

        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => setSelectedSentiment('negative')}
          className={[
            'flex h-11 w-11 items-center justify-center rounded-full transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2',
            selectedSentiment === 'negative'
              ? 'bg-red-100 text-red-700 scale-110 shadow-sm'
              : 'bg-gray-100 text-op-text-tertiary hover:bg-gray-200 active:scale-95',
            isSubmitting && 'opacity-50 cursor-not-allowed',
          ].join(' ')}
          aria-label="Thumbs down"
        >
          <ThumbsDown size={20} strokeWidth={1.5} />
        </button>
      </div>

      {/* Comment + Anonymity + Submit (only shown after selecting a sentiment) */}
      {selectedSentiment && (
        <div className="mt-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Comment Textarea */}
          <div>
            <textarea
              value={comment}
              onChange={(e) => {
                if (e.target.value.length <= COMMENT_MAX_LENGTH) {
                  setComment(e.target.value);
                }
              }}
              disabled={isSubmitting}
              placeholder="Add a comment (optional)"
              rows={2}
              className={[
                'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-op-text-primary',
                'placeholder:text-op-text-tertiary',
                'focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green',
                'resize-none',
                isSubmitting && 'opacity-50 cursor-not-allowed',
              ].join(' ')}
            />
            <div className="mt-1 flex justify-end">
              <span
                className={[
                  'text-[11px]',
                  isAtLimit
                    ? 'font-semibold text-amber-600'
                    : isNearLimit
                      ? 'text-amber-500'
                      : 'text-op-text-tertiary',
                ].join(' ')}
              >
                {commentLength}/{COMMENT_MAX_LENGTH}
              </span>
            </div>
          </div>

          {/* Anonymous Toggle (not in compact mode) */}
          {!compact && (
            <label className="flex cursor-pointer items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  disabled={isSubmitting}
                  className="peer sr-only"
                />
                <div
                  className={[
                    'h-5 w-9 rounded-full transition-colors',
                    isAnonymous ? 'bg-guild-green' : 'bg-gray-300',
                    isSubmitting && 'opacity-50',
                  ].join(' ')}
                />
                <div
                  className={[
                    'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                    isAnonymous && 'translate-x-4',
                  ].join(' ')}
                />
              </div>
              <EyeOff size={14} strokeWidth={1.5} className="text-op-text-tertiary" />
              <span className="text-xs text-op-text-secondary">Submit anonymously</span>
            </label>
          )}

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedSentiment}
            className={[
              'flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5',
              'text-sm font-semibold text-white transition-all',
              'bg-guild-green hover:bg-guild-green/90 active:scale-[0.98]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>

          {/* Error Message */}
          {submitMutation.isError && (
            <p className="text-center text-xs text-red-500">
              {(submitMutation.error as { code?: string })?.code === 'CONFLICT'
                ? 'You have already submitted feedback for this item.'
                : 'Something went wrong. Please try again.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
